import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import Sidebar from "./Sidebar";
import { toast, ToastContainer } from "react-toastify";

import TopNavbar from "../top-navbar/TopNav";
import { CiSearch } from "react-icons/ci";
import {
  getAllCandidates,
  postQuickBgvCandidate,
  updateQuickBgvCandidate,
  getQuickBGVCandidateInfo, // Imported API service
} from "../../services/employee/api";

import { FaEye, FaEdit } from "react-icons/fa";
import { ArrowLeft } from "lucide-react";

import "../../styles/employee/QuickBGC.css";

const QuickBGV = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [entriesPerPage, setEntriesPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [isLastPage, setIsLastPage] = useState(false);
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const isFirstLoad = useRef(true);
  const [showModal, setShowModal] = useState(false);
  const [sortConfig, setSortConfig] = useState({
    key: null,
    direction: "desc",
  });

  const [refreshKey, setRefreshKey] = useState(0);
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    uniqueId: "",
    name: "",
    email: "",
    mobile: "",
    dob: "",
    createdBy: "",
  });
  const [formErrors, setFormErrors] = useState({});
  const [isEditMode, setIsEditMode] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Calculate Overall Status based on API verification fields
  const calculateOverallStatus = (aadhaar, uan, criminal) => {
    const statuses = [aadhaar, uan, criminal].map(
      (s) => s?.toUpperCase() || ""
    );

    const isAllCompleted = statuses.every((s) => s === "COMPLETED");
    if (isAllCompleted) return "Completed";

    const hasAnyProgressOrComplete = statuses.some(
      (s) => s === "COMPLETED" || s === "WORK IN PROGRESS" || s === "IN PROGRESS" || s === "INPROGRESS"
    );
    if (hasAnyProgressOrComplete) return "In Progress";

    return "Pending";
  };

useEffect(() => {
  let cancelled = false;

  const fetchData = async () => {
    try {
      if(isFirstLoad.current) {
        setLoading(true);
      }
      setError(null);
 
  const response = await getAllCandidates(
  searchTerm,
  currentPage - 1,
  entriesPerPage,
  sortConfig.key || "uniqueId",
  sortConfig.direction
);

      if (cancelled) {
        return;
      }

      const pageData = response?.data?.data;

      const apiData = pageData?.candidates || [];

      setTotalPages(pageData?.totalPages || 0);
      setTotalElements(pageData?.totalElements || 0);
      setIsLastPage(pageData?.isLastPage ?? true);

      // Fetch verification status for each candidate
      // returned by the current page
      const formattedData = await Promise.all(
        apiData.map(async (item) => {
          let overallStatus = "Pending";

          try {
            const statusRes =
              await getQuickBGVCandidateInfo(item.candidateId);

            const statusData =
              statusRes?.data?.data ||
              statusRes?.data ||
              {};

            const {
              aadhaarStatus,
              uanStatus,
              criminalStatus,
            } = statusData;

            overallStatus = calculateOverallStatus(
              aadhaarStatus,
              uanStatus,
              criminalStatus
            );
          } catch (err) {
            overallStatus = "Pending";
          }

          return {
            uniqueId: item.candidateId,
            name: item.fullname,
            email: item.email,
            mobile: item.mobileNumber,
            dob: item.dateOfBirth,
            createdBy: item.createdBy,
            status: overallStatus,
          };
        })
      );

      if (!cancelled) {
        setCandidates(formattedData);
      }

    } catch (err) {
      if (!cancelled) {
        toast.error("Failed to fetch candidates");
        setError("Failed to fetch candidates");
        setCandidates([]);
        setTotalPages(0);
        setTotalElements(0);
        setIsLastPage(true);
      }
    } finally {
  if (!cancelled) {
    setLoading(false);
    isFirstLoad.current = false;
  }
}
  };

  const delay = setTimeout(() => {
    fetchData();
  }, 300);

  return () => {
    clearTimeout(delay);
    cancelled = true;
  };

}, [searchTerm, currentPage, entriesPerPage, sortConfig.key, sortConfig.direction, refreshKey]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;

     const updatedValue =
    name === "name"
      ? value.charAt(0).toUpperCase() + value.slice(1)
      : value;

    setFormData((prev) => ({
      ...prev,
      [name]: updatedValue,
    }));

    setFormErrors((prev) => ({
      ...prev,
      [name]: "",
    }));
  };

  const handleEditClick = (candidate) => {
    setIsEditMode(true);

    setFormData({
      uniqueId: candidate.uniqueId,
      name: candidate.name,
      email: candidate.email,
      mobile: candidate.mobile,
      dob: formatToInputDate(candidate.dob),
      createdBy: candidate.createdBy,
    });
    setFormErrors({});
    setShowModal(true);
  };

  const handleStartBGC = (candidateId) => {
    navigate(`/employee/quickbgv-dashboard/${candidateId}`, {
      state: { candidateId },
    });
  };

  // UI → Backend
  const formatToBackendDate = (date) => {
    if (!date) return "";
    const [year, month, day] = date.split("-");
    return `${day}-${month}-${year}`;
  };

  // Backend → UI
  const formatToInputDate = (date) => {
    if (!date) return "";
    const [day, month, year] = date.split("-");
    return `${year}-${month}-${day}`;
  };

  const validateForm = () => {
    const errors = {};

    if (!formData.name.trim()) {
      errors.name = "Full name is required";
    } else if (formData.name.length > 40) {
      errors.name = "Max 40 characters allowed";
    } else if (!/^[A-Za-z ]+$/.test(formData.name)) {
      errors.name = "Only letters and spaces allowed";
    }

    if (!formData.email.trim()) {
      errors.email = "Email is required";
    } else if (!/^\S+@\S+\.\S+$/.test(formData.email)) {
      errors.email = "Invalid email format";
    }
    else if (!/^[a-zA-Z0-9._%+-]+@gmail\.com$/.test(formData.email)) {
  errors.email = "Please enter a valid Gmail address";
}

    if (!formData.dob) {
      errors.dob = "Date of birth is required";
    }

    if (!formData.mobile.trim()) {
      errors.mobile = "Mobile number is required";
    } else if (!/^[0-9]{10}$/.test(formData.mobile)) {
      errors.mobile = "Must be exactly 10 digits";
    }

    return errors;
  };

 const handleSubmit = async (e) => {
    e.preventDefault();
    const errors = validateForm();
    setFormErrors(errors);

    if (Object.keys(errors).length > 0) {
      return;
    }

    const data = {
      fullname: formData.name,
      email: formData.email,
      mobileNumber: formData.mobile,
      dateOfBirth: formatToBackendDate(formData.dob),
    };

    try {
      setIsSubmitting(true); // 1. Start loading state

      if (isEditMode) {
        const response = await updateQuickBgvCandidate(data, formData.uniqueId);
       
        setRefreshKey((prev) => prev + 1);

        toast.success("Candidate updated successfully");
      }
      
      else {
  await postQuickBgvCandidate(data);

  setRefreshKey((prev) => prev + 1);

  toast.success("Candidate added successfully");
}

      setShowModal(false);
      setFormData({
        uniqueId: "",
        name: "",
        email: "",
        mobile: "",
        dob: "",
        createdBy: "",
      });
       }

catch (error) {
  const message =
    error?.response?.data?.message ||
    error?.response?.data?.error ||
    "Something went wrong";

  if (!isEditMode && message.toLowerCase().includes("email")) {
    setFormErrors((prev) => ({
      ...prev,
      email: "Email already exists",
    }));

    toast.error("Email already exists");
    return;
  }

  toast.error(message);
}

    
    finally {
      setIsSubmitting(false);
    }
  };
  
const currentEntries = candidates;

  const handlePageChange = (pageNumber) => {
    if (pageNumber >= 1 && pageNumber <= totalPages) {
      setCurrentPage(pageNumber);
    }
  };

  const handleEntriesPerPageChange = (e) => {
    setEntriesPerPage(Number(e.target.value));
    setCurrentPage(1);
  };

  const handleSort = (key, direction) => {
  setSortConfig({
    key,
    direction,
  });

  setCurrentPage(1);
};

const SortIcon = ({ column }) => {
  return (
    <span className="sort-icons">
      <span
        className={`sort-icon-button ${
          sortConfig.key === column && sortConfig.direction === "asc"
            ? "active"
            : ""
        }`}
        style={{
    color: "white",
    opacity: 1,
    WebkitTextFillColor: "white",
    textShadow: "0 0 1px white",
  }}

        onClick={(e) => {
          e.stopPropagation();
          handleSort(column, "asc");
        }}
        title="Sort ascending"
        role="button"
        tabIndex={0}
      >
        ▲
      </span>

      <span
        className={`sort-icon-button ${
          sortConfig.key === column && sortConfig.direction === "desc"
            ? "active"
            : ""
        }`}
       style={{
    color: "white",
    opacity: 1,
    WebkitTextFillColor: "white",
    textShadow: "0 0 1px white",
  }}
        onClick={(e) => {
          e.stopPropagation();
          handleSort(column, "desc");
        }}
        title="Sort descending"
        role="button"
        tabIndex={0}
      >
        ▼
      </span>
    </span>
  );
};

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case "Completed":
        return "badge-completed";
      case "In Progress":
        return "badge-in-progress";
      default:
        return "badge-pending";
    }
  };

  return (
    <div className="dashboard-app-container">
      <Sidebar />

      <div className="assigned-dashboard-right-panel">
        <TopNavbar />

        <div className="content-area-wrapper">
          <main className="assigned-main-content">

    <button
        type="button"
        className="quickbgv-back-button"
        onClick={() => navigate('/employee/dashboard')}
    >
        <ArrowLeft size={18} />
        <span>Back</span>
    </button>

    <div className="quickbgv-header">
        <h3 className="assigne-candidate-list-header">
            Quick BGV List
        </h3>

              <button
                className="add-candidate-btn"
                onClick={() => {
                  setIsEditMode(false);
                  setFormErrors({});
                  setFormData({
                    uniqueId: "",
                    name: "",
                    email: "",
                    mobile: "",
                    dob: "",
                    createdBy: "",
                  });
                  setShowModal(true);
                }}
              >
                + Add Candidate
              </button>
            </div>

            <section className="assigne-candidate-section">
              {/* Controls */}
              <div className="table-controls">
                <label className="entries-label">
                  Entries per page:
                  <select
                    value={entriesPerPage}
                    onChange={handleEntriesPerPageChange}
                    className="show-entries-dropdown"
                  >
                     <option value={10}>10</option>
                     <option value={25}>25</option>
                     <option value={50}>50</option>
                     <option value={100}>100</option>
                    {/* <option value={20}>20</option> */}
                  </select>
                </label>

                <div className="search-bar-container">

                     <input
                     type="text"
                   placeholder="Search candidates..."
                    className="search-bar-input"
                    value={searchTerm}
                     onChange={(e) => {
                     setSearchTerm(e.target.value);
                      setCurrentPage(1);
                         }}
                        />

                  <span className="search-bar-icon">
                    <CiSearch />
                  </span>
                </div>
              </div>

              {/* Table */}
              {loading ? (
                <p className="loading-message">Loading candidates...</p>
              ) : error ? (
                <p className="error-message">{error}</p>
              ) : (
                <>
                  <div className="assigne-candidate-table-container">
                    <table className="assigne-candidate-table">
                      <thead>
                        <tr>     
                          <th>
                      Candidate ID <SortIcon column="uniqueId" />
                        </th>

                        <th>
                    Full Name <SortIcon column="name" />
                         </th>
                          <th>Email</th>
                          <th>Mobile</th>
                          <th>DOB</th>
                           <th>
                     Created By <SortIcon column="createdBy" />
                           </th>

                          <th>Verification</th>
                          <th onClick={() => handleSort("status")}>
                            Status <SortIcon column="status" />
                          </th>
                          <th>Actions</th>
                        </tr>
                      </thead>

                      <tbody>
                        {currentEntries.length > 0 ? (
                          currentEntries.map((c) => (
                            <tr key={c.uniqueId}>
                              <td>{c.uniqueId}</td>
                              <td>{c.name}</td>
                              <td>{c.email}</td>
                              <td>{c.mobile}</td>
                              <td>{c.dob}</td>
                              <td>{c.createdBy}</td>

                            {/* Verification Column */}
                              <td>
                                <button
                                  className={`start-bgc-button ${c.status === "Completed" ? "disabled-btn" : ""}`}
                                  onClick={() => handleStartBGC(c.uniqueId)}
                                  disabled={c.status === "Completed"}
                                >
                                  
                                  Start BGC
                                </button>
                              </td>

                              {/* Status Column with Dynamic Colors */}
                              <td>
                                <span className={`status-badge ${getStatusBadgeClass(c.status)}`}>
                                  {c.status}
                                </span>
                              </td>

                              <td>
                                <div className="quickbgv-action-icons">
                                  <FaEdit
                                    title="Edit"
                                    className="quickbgv-icon edit"
                                    onClick={() => handleEditClick(c)}
                                  />
                                  <FaEye
                                    title="View Report"
                                    className="quickbgv-icon view"
                                    onClick={() =>
                                      navigate(
                                        `/employee/quickbgv-report/${c.uniqueId}`
                                      )
                                    }
                                  />
                                </div>
                              </td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                           
                            <td colSpan="9" className="text-center">
                           {searchTerm.trim()
                            ? "No records found"
                             : "No candidates found"}
                              </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>

                  {/* Pagination */}
                  <div className="pagination-container">
                    <div className="pagination-info">
                     Showing{" "}
                          {totalElements === 0
                              ? 0
                      : (currentPage - 1) * entriesPerPage + 1}{" "}
                            to{" "}
                         {Math.min(
                       currentPage * entriesPerPage,
                           totalElements
                            )}{" "}
                        of {totalElements} entries
                             </div>
                    <div className="pagination-buttons">
                      <button
                        className="pagination-button"
                        onClick={() => handlePageChange(currentPage - 1)}
                        disabled={currentPage === 1}
                      >
                        Previous
                      </button>
                      {Array.from({ length: totalPages }, (_, i) => i + 1).map(
                        (page) => (
                          <button
                            key={page}
                            className={`pagination-button ${
                              currentPage === page ? "active" : ""
                            }`}
                            onClick={() => handlePageChange(page)}
                          >
                            {page}
                          </button>
                        )
                      )}
                      <button
                        className="pagination-button"
                        onClick={() => handlePageChange(currentPage + 1)}
                        disabled={currentPage === totalPages || totalPages === 0}
                      >
                        Next
                      </button>
                    </div>
                  </div>
                </>
              )}
            </section>
          </main>
        </div>
      </div>

      {showModal && (
        <div className="quickbgv-modal-overlay">
          <div className="quickbgv-modal-container">
            <div className="quickbgv-modal-header">
              <h3>{isEditMode ? "Edit Candidate" : "Add Candidate"}</h3>
              <button
                className="close-icon"
                onClick={() => {
                  setShowModal(false);
                  setIsEditMode(false);
                  setFormErrors({});
                  setFormData({
                    uniqueId: "",
                    name: "",
                    email: "",
                    mobile: "",
                    dob: "",
                    createdBy: "",
                  });
                }}
              >
                ✖
              </button>
            </div>

            <div className="quickbgv-modal-body">
              <form
                className="quickbgv-candidate-form"
                onSubmit={handleSubmit}
              >
                <div className="quickbgv-form-grid">
                  <div className="quickbgv-form-group">
                    <label>Full Name *</label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      placeholder="Enter full name"
                    />
                    {formErrors.name && (
                      <p className="error-text"> {formErrors.name}</p>
                    )}
                  </div>

                  <div className="quickbgv-form-group">
                    <label>Email *</label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      placeholder="Enter your gmail address"
                    />
                    {formErrors.email && (
                      <p className="error-text"> {formErrors.email}</p>
                    )}
                  </div>

                  <div className="quickbgv-form-group">
                    <label>Date of Birth *</label>
                    <input
                      type="date"
                      name="dob"
                      value={formData.dob}
                      onChange={handleInputChange}
                    />
                    {formErrors.dob && (
                      <p className="error-text"> {formErrors.dob}</p>
                    )}
                  </div>

                  <div className="quickbgv-form-group">
                    <label>Mobile Number *</label>
                    <input
                      type="tel"
                      name="mobile"
                      value={formData.mobile}
                      onChange={handleInputChange}
                      placeholder="Enter mobile number"
                    />
                    {formErrors.mobile && (
                      <p className="error-text"> {formErrors.mobile}</p>
                    )}
                  </div>
                </div>

                <div className="quickbgv-modal-actions">
                  <button
                    type="button"
                    className="quickbgv-cancel-btn"
                    onClick={() => {
                      setShowModal(false);
                      setIsEditMode(false);
                      setFormErrors({});
                      setFormData({
                        uniqueId: "",
                        name: "",
                        email: "",
                        mobile: "",
                        dob: "",
                        createdBy: "",
                      });
                    }}
                  >
                    Cancel
                  </button>

                 <button
                    type="submit"
                    className="quickbgv-save-btn"
                    disabled={isSubmitting}
                  >
                    {isSubmitting
                      ? isEditMode
                        ? "Updating..."
                        : "Saving..."
                      : isEditMode
                      ? "Update Candidate"
                      : "Save Candidate"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        closeOnClick
        pauseOnHover
      />
    </div>
  );
};

export default QuickBGV;